import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { LancamentosRejeitadosOutrasDespesas } from '@gestor-web/@core/api/ords/model';
import { LayoutActions, OperadorasActions } from '@gestor-web/@core/store/actions';
import { LayoutSelectors, OperadorasSelectors } from '@gestor-web/@core/store/selectors';
import { filterToQueryString } from '@gestor-web/@core/utils';
import { concatLatestFrom } from '@ngrx/operators';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';

import { Store } from '@ngrx/store';
import { SelectItem } from 'primeng/api';
import { CompetenciaPipe } from '@gestor-web/app/shared/pipes/competencia.pipe';
import { CpfCnpjPipe } from '@gestor-web/app/shared/pipes/cpf-ou-cnpj.pipe';
import { GeralService, NotificationService, PermissaoService } from '@gestor-web/app/shared/services';
import { GridService } from '@gestor-web/app/shared/services/grid.service';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { GridOptions } from 'ag-grid-enterprise';
import { AgFilterUtils, buildGridOptions, exportGridAsExcel } from '@gestor-web/app/shared/components';
import { normalizeGuiaMotivo } from '@gestor-web/app/shared/utils/guias.utils';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { filter, first, takeUntil } from 'rxjs/operators';
import { LancamentosRejeitadosOutrasDespesasService } from './lancamentos-rejeitados-outras-despesas.service';

const GRID_KEY = 'ANALISES.LANCAMENTOS_REJEITADOS_OUTRAS_DESPESAS';

const MULTILINE_COLUMN: Pick<
  ColDef,
  'wrapText' | 'autoHeight' | 'cellClass' | 'filterValueGetter' | 'getQuickFilterText'
> = {
  wrapText: true,
  autoHeight: true,
  cellClass: 'cell-multiline',
  filterValueGetter: params => {
    const field = params.colDef.field;
    if (!field) return '';
    const value = (params.data as any)?.[field];
    return normalizeMultilineValue(value);
  },
  getQuickFilterText: params => normalizeMultilineValue(params.value),
};

const normalizeMultilineValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value
      .map(v => String(v).trim())
      .filter(Boolean)
      .join(' | ');
  }
  if (typeof value === 'string') {
    return value
      .replace(/\r?\n+/g, ' | ')
      .replace(/\s*\|\s*/g, ' | ')
      .trim();
  }
  if (value == null) return '';
  return String(value);
};

const COLUMNS: ColDef[] = [
  {
    headerName: '003-Competência',
    field: 'nr_competencia',
    type: 'competencia',
    width: 160,
  },
  {
    headerName: '112-ID Tipo de registro',
    field: 'nm_tp_registro',
    width: 160,
  },
  {
    headerName: '114-Tipo de identificação - CPF/CNPJ',
    field: 'nm_id_cnpj_cpf_exec',
    width: 240,
  },
  {
    headerName: '115-Nr. CNPJ/CPF do recebedor',
    field: 'nr_cnpj_cpf_exec',
    type: 'cpfCnpj',
    width: 240,
  },
  {
    headerName: '113-Data processamento',
    field: 'dt_processamento',
    type: 'date',
    width: 220,
  },
  {
    headerName: '116-Valor total informado',
    field: 'vl_informado',
    type: 'money',
    width: 160,
  },
  {
    headerName: '117-Valor total glosa',
    field: 'vl_glosa',
    type: 'money',
    width: 140,
  },
  {
    headerName: '118-Valor total pago',
    field: 'vl_pago',
    type: 'money',
    width: 140,
  },
  {
    headerName: 'Nome arquivo no Gestor IDSS',
    field: 'nm_arquivo',
    width: 240,
  },
  {
    headerName: 'Código motivo arquivo',
    field: 'cd_motivo_arquivo',
    width: 200,
  },
  {
    headerName: 'Motivo arquivo',
    field: 'ds_motivo_arquivo',
    width: 280,
  },
  {
    headerName: 'Rejeição do campo',
    field: 'lt_campo_guia',
    width: 240,
    ...MULTILINE_COLUMN,
  },
  {
    headerName: 'Rejeição da guia',
    field: 'lt_motivo_guia',
    width: 240,
    ...MULTILINE_COLUMN,
  },
];

@Component({
    templateUrl: './lancamentos-rejeitados-outras-despesas.component.html',
    styleUrls: ['./lancamentos-rejeitados-outras-despesas.component.scss'],
    providers: [LancamentosRejeitadosOutrasDespesasService, DecimalPipe],
    standalone: false
})
export class LancamentosRejeitadosOutrasDespesasComponent implements OnInit, OnDestroy {
  readonly GRID_KEY = GRID_KEY;

  @ViewChild('agGrid') agGrid: AgGridAngular<Row>;

  gridOptions: GridOptions<Row>;

  pageReady$ = new Subject<void>();
  filterReady$ = new Subject<boolean>();
  firstLoad = true;
  filterChanged = true;

  podeGerenciarVisoes = false;

  operadora: string;
  operadoraNome: string;
  competenciaRange: string[];
  appliedCompetenciaRange: string[] = [];

  rowData: Row[];

  pageTooltip = 'RELATORIOS_PERSONALIZADOS.LANCAMENTOS_REJEITADOS_OUTRAS_DESPESAS';

  motivoArquivoOptions: SelectItem<string>[] = [];
  selectedRejeicaoArquivo: string = null;
  selectedMotivoArquivo: string = null;

  fullscreen$: Observable<boolean>;
  gridHeight$: Observable<string>;

  private ngDestroyed$ = new Subject<void>();

  constructor(
    private permissaoService: PermissaoService,
    private service: LancamentosRejeitadosOutrasDespesasService,
    private datePipe: DatePipe,
    private decimalPipe: DecimalPipe,
    private competenciaPipe: CompetenciaPipe,
    private cpfCnpjPipe: CpfCnpjPipe,
    private geralService: GeralService,
    private notificationService: NotificationService,
    private gridService: GridService,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.fullscreen$ = this.store.select(LayoutSelectors.selectFullscreen);
    this.gridHeight$ = of('100%');
  }

  ngOnInit(): void {
    const ano = new Date().getFullYear();
    this.competenciaRange = [`${ano}01`, `${ano}12`];

    this.service.getFiltro().subscribe(items => {
      this.motivoArquivoOptions = items.map(item => ({
        label: item.motivo_glosa,
        value: String(item.cd_motivo_glosa),
      }));
    });

    this.store
      .select(OperadorasSelectors.selectWorkspaceOperadora)
      .pipe(
        filter(op => !!op),
        concatLatestFrom(() => this.store.select(OperadorasSelectors.selectWorkspaceParams)),
        takeUntil(this.ngDestroyed$),
      )
      .subscribe(([operadora, params]) => {
        this.operadora = operadora.cd_registro_ans;
        this.operadoraNome = operadora.sg_operadora;
        this.competenciaRange = [
          params.competencia ? '' + params.competencia : this.competenciaRange[0],
          params.competencia_final ? '' + params.competencia_final : this.competenciaRange[1],
        ];

        setTimeout(() => this.pageReady$.next());
      });

    combineLatest([this.route.queryParams, this.pageReady$])
      .pipe(first())
      .subscribe(([params, _]) => this.applyQueryFilters(params));

    combineLatest([this.filterReady$, this.pageReady$])
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(([hasFilter, _]) => {
        if (!this.firstLoad || hasFilter) {
          this.reloadData();
        }
      });

    this.permissaoService
      .checkAcessoFeature(FUNCIONALIDADE.GERENCIAR_VISOES)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(podeGerenciarVisoes => {
        this.podeGerenciarVisoes = podeGerenciarVisoes;
        this.configureGrid();
      });
  }

  private configureGrid() {
    this.gridOptions = buildGridOptions<Row>(
      {
        columnDefs: COLUMNS,
        pipes: {
          date: this.datePipe,
          decimal: this.decimalPipe,
          competencia: this.competenciaPipe,
          cpfCnpj: this.cpfCnpjPipe,
        },
        views: {
          gridKey: GRID_KEY,
          hide: !this.podeGerenciarVisoes,
        },
        exportExcelFn: () => this.exportExcel(),
      },
      {
        context: {
          parentComponent: this,
        },
      },
    );
  }

  applyQueryFilters(params: Params) {
    const { competencia, competencia_final, motivo_arquivo, critica_guia, visao, sort, extraFilters } = params;
    let hasFilter = false;

    if (competencia) {
      hasFilter = true;
      this.competenciaRange[0] = competencia;
    }

    if (competencia_final) {
      hasFilter = true;
      this.competenciaRange[1] = competencia_final;
    }

    if (motivo_arquivo) {
      hasFilter = true;
      this.selectedMotivoArquivo = motivo_arquivo;
    }

    if (critica_guia) {
      hasFilter = true;
      this.selectedRejeicaoArquivo = critica_guia;
    }

    if (sort || extraFilters) {
      hasFilter = true;
    }

    const filterModel = this.gridService.getDecodedFilter(extraFilters);
    setTimeout(() => this.gridService.applyVisaoInicial(GRID_KEY, this.agGrid, visao, sort, filterModel));

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });

    this.filterReady$.next(hasFilter);
  }

  resetPagination() {
    this.filterChanged = true;
  }

  onFilterChange() {
    this.resetPagination();
  }

  competenciaChanged(range: string[]) {
    this.competenciaRange = range;
    this.store.dispatch(
      OperadorasActions.changeWorkspaceParams({
        partialParams: {
          competencia: +range[0],
          competencia_final: +range[1],
        },
      }),
    );
    this.resetPagination();
  }

  onSearch(value: string) {
    this.agGrid?.api?.updateGridOptions({ quickFilterText: value });
  }

  reloadData(applyPendingFilters: boolean = false) {
    const shouldApplyFilters = applyPendingFilters || !this.appliedCompetenciaRange?.length;
    if (shouldApplyFilters) {
      this.appliedCompetenciaRange = [...this.competenciaRange];
      this.filterChanged = false;
    }

    if (this.firstLoad) {
      this.firstLoad = false;
      this.agGrid?.api?.setGridOption('loading', false);
      setTimeout(() => this.agGrid?.api?.setGridOption('loading', true));
    } else {
      this.agGrid?.api?.setGridOption('loading', true);
    }

    if (!this.appliedCompetenciaRange[0] || !this.appliedCompetenciaRange[1]) {
      this.rowData = [];
      this.agGrid?.api?.setGridOption('loading', false);
      return;
    }

    this.service
      .getDataGoApi(
        this.operadora,
        this.appliedCompetenciaRange[0],
        this.appliedCompetenciaRange[1],
        this.selectedMotivoArquivo,
        this.selectedRejeicaoArquivo,
      )
      .subscribe({
        next: ({ items }) => {
          this.rowData = (items ?? []).map(item => this.normalizeRow(item));
        },
        error: error => this.notificationService.handleError(error),
        complete: () => this.agGrid?.api?.setGridOption('loading', false),
      });
  }

  private normalizeRow(item: LancamentosRejeitadosOutrasDespesas): Row {
    return {
      ...item,
      nm_tp_registro: this.geralService.buildNomeDeDominio(item.tp_registro, 'dm_statusTransacaoMonitor'),
      nm_id_cnpj_cpf_exec: this.geralService.buildNomeDeDominio(item.id_cnpj_cpf_exec, 'MON_ID_CNPJ_CPF'),
      lt_motivo_guia: normalizeGuiaMotivo(item.lt_motivo_guia),
    };
  }

  toggleFullscreen() {
    this.store.dispatch(LayoutActions.toggleFullscreen({}));
  }

  exportExcel(): void {
    const competencia = this.appliedCompetenciaRange?.length ? this.appliedCompetenciaRange : this.competenciaRange;
    const fileNamePrefix = `lancamentos-rejeitados-outras-despesas-${this.operadoraNome}-${competencia[0]}-${competencia[1]}`;

    const headers = [
      'Lançamentos Rejeitados de Outras Despesas Assistenciais',
      `${this.operadoraNome} - ${this.operadora}`,
      'Competência: ' + AgFilterUtils.competenciaRangeToString(competencia),
    ];

    exportGridAsExcel({
      api: this.agGrid.api,
      headers,
      fileNamePrefix,
    });
  }

  onCopyUrlFilters() {
    const visao = this.gridService.getCurrentVisao(GRID_KEY);
    const sort = this.gridService.getSimpleSort(this.agGrid);
    const extraFilters = this.gridService.getEncodedFilter(this.agGrid);
    const competencia = this.appliedCompetenciaRange?.length ? this.appliedCompetenciaRange : this.competenciaRange;

    const urlFilter = {
      competencia: competencia[0],
      competencia_final: competencia[1],
      motivo_arquivo: this.selectedMotivoArquivo,
      critica_guia: this.selectedRejeicaoArquivo,
      visao: visao?.cd_visao,
      sort,
      extraFilters,
    };

    const url = filterToQueryString(urlFilter);
    navigator.clipboard.writeText(url);
    this.notificationService.info('Endereço com filtros copiado');
  }

  ngOnDestroy() {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}

interface Row extends LancamentosRejeitadosOutrasDespesas {
  nm_tp_registro: string;
  nm_id_cnpj_cpf_exec: string;
}
